/**
 * Hausverwaltungen — der Bereich des Betreibers.
 *
 * Eine Hausverwaltung anlegen heißt: Name, bei Bedarf der iDWELL-Schlüssel
 * (wird sofort geprüft) und die E-Mail der Person, die dort als Erste
 * arbeiten soll. Sie bekommt einen Link, setzt ihr Passwort und lädt dann
 * selbst ihre Kollegen ein.
 */

import { useEffect, useState } from 'react';

import { api, datum, mandantWaehlen } from '../../lib/api';
import type { Mandant, Mitarbeiter } from '../../lib/typen';
import { Linkkasten } from './Einstellungen';

type MandantMitZahlen = Mandant & { benutzer: number; pruefungen: number };

export function Mandanten({ onMeldung, onGeaendert }: { onMeldung: (t: string) => void; onGeaendert: () => void }) {
  const [liste, setListe] = useState<MandantMitZahlen[] | null>(null);
  const [neu, setNeu] = useState(false);
  const [form, setForm] = useState({ name: '', kurzname: '', apiKey: '', adminEmail: '', adminName: '' });
  const [laeuft, setLaeuft] = useState(false);
  const [zugangslink, setZugangslink] = useState<{ fuer: string; link: string; meldung: string } | null>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const [benutzer, setBenutzer] = useState<Record<string, Mitarbeiter[]>>({});
  const [einladung, setEinladung] = useState({ email: '', name: '' });
  const [betreiber, setBetreiber] = useState<Mitarbeiter[]>([]);
  const [neuerBetreiber, setNeuerBetreiber] = useState({ email: '', name: '' });

  async function laden() {
    try {
      setListe(await api.admin.mandanten());
      setBetreiber(await api.admin.betreiber());
    } catch (g) {
      setListe([]);
      onMeldung((g as Error).message);
    }
  }

  useEffect(() => {
    void laden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function anlegen(e: React.FormEvent) {
    e.preventDefault();
    setLaeuft(true);
    try {
      const a = await api.admin.mandantAnlegen({
        name: form.name.trim(),
        kurzname: form.kurzname.trim() || undefined,
        apiKey: form.apiKey.trim() || undefined,
        adminEmail: form.adminEmail.trim() || undefined,
        adminName: form.adminName.trim() || undefined,
      });
      onMeldung(`„${a.mandant.name}" angelegt.`);
      if (a.zugang) setZugangslink({ fuer: form.adminEmail.trim(), link: a.zugang.link, meldung: a.zugang.meldung });
      setForm({ name: '', kurzname: '', apiKey: '', adminEmail: '', adminName: '' });
      setNeu(false);
      await laden();
      onGeaendert();
    } catch (g) {
      onMeldung((g as Error).message);
    } finally {
      setLaeuft(false);
    }
  }

  async function aufklappen(id: string) {
    if (offen === id) {
      setOffen(null);
      return;
    }
    setOffen(id);
    try {
      setBenutzer((alt) => ({ ...alt, [id]: [] }));
      const b = await api.admin.benutzer(id);
      setBenutzer((alt) => ({ ...alt, [id]: b }));
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  async function einladen(mandantId: string, e: React.FormEvent) {
    e.preventDefault();
    try {
      const a = await api.admin.benutzerEinladen(mandantId, einladung.email.trim(), einladung.name.trim());
      setZugangslink({ fuer: einladung.email.trim(), link: a.link, meldung: a.meldung });
      setEinladung({ email: '', name: '' });
      setBenutzer((alt) => ({ ...alt, [mandantId]: [] }));
      setBenutzer((alt) => ({ ...alt, [mandantId]: alt[mandantId] }));
      const b = await api.admin.benutzer(mandantId);
      setBenutzer((alt) => ({ ...alt, [mandantId]: b }));
      await laden();
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  async function betreiberEinladen(e: React.FormEvent) {
    e.preventDefault();
    try {
      const a = await api.admin.betreiberEinladen(neuerBetreiber.email.trim(), neuerBetreiber.name.trim());
      setZugangslink({ fuer: neuerBetreiber.email.trim(), link: a.link, meldung: a.meldung });
      setNeuerBetreiber({ email: '', name: '' });
      setBetreiber(await api.admin.betreiber());
    } catch (g) {
      onMeldung((g as Error).message);
    }
  }

  if (liste === null) return <div className="wrap"><div className="skelett" style={{ height: 160 }} /></div>;

  return (
    <div className="wrap" style={{ maxWidth: 960 }}>
      <div className="seitenkopf">
        <div>
          <h2 style={{ margin: 0 }}>Hausverwaltungen</h2>
          <p className="s" style={{ margin: '4px 0 0' }}>Jede Hausverwaltung ist ein Mandant mit eigenem iDWELL-Schlüssel, eigenen Prüfungen und eigenen Mitarbeitern.</p>
        </div>
        <div className="spacer" />
        <button className="btn primary" onClick={() => setNeu(true)}>
          + Hausverwaltung
        </button>
      </div>

      {zugangslink && <Linkkasten fuer={zugangslink.fuer} link={zugangslink.link} meldung={zugangslink.meldung} onZu={() => setZugangslink(null)} />}

      {liste.length === 0 && (
        <div className="leer card">
          <h3>Noch keine Hausverwaltung</h3>
          <p>Legen Sie die erste an — mit dem iDWELL-Schlüssel der Hausverwaltung und der E-Mail der Person, die dort beginnt.</p>
        </div>
      )}

      {liste.map((m) => (
        <div key={m.id} className="card" style={{ padding: 16, marginBottom: 10 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <b style={{ fontSize: 15 }}>{m.name}</b>
              {m.kurzname && <span className="s" style={{ margin: '0 0 0 8px' }}>({m.kurzname})</span>}
              <div className="s" style={{ margin: 0 }}>
                {m.pruefungen} Prüfungen · {m.benutzer} Mitarbeiter · angelegt {datum(m.erstelltAm)}
              </div>
            </div>
            <span className={`pill ${m.idwellVerbunden ? 'ok' : 'w'}`}>
              <span className="dot" />
              {m.idwellVerbunden ? 'iDWELL verbunden' : 'ohne iDWELL'}
            </span>
            {!m.aktiv && <span className="chip warn">deaktiviert</span>}
            <button
              className="btn ghost sm"
              onClick={() => {
                mandantWaehlen(m.id);
                onGeaendert();
                window.history.pushState({}, '', '/verwaltung');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
            >
              Als diese arbeiten
            </button>
            <button className="btn ghost sm" onClick={() => aufklappen(m.id)}>
              {offen === m.id ? 'Zuklappen' : 'Mitarbeiter'}
            </button>
            <button
              className="btn ghost sm"
              onClick={async () => {
                await api.admin.mandantAendern(m.id, { aktiv: !m.aktiv });
                await laden();
              }}
            >
              {m.aktiv ? 'Deaktivieren' : 'Aktivieren'}
            </button>
          </div>

          {offen === m.id && (
            <div style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
              <table className="schlicht">
                <tbody>
                  {(benutzer[m.id] ?? []).map((b) => (
                    <tr key={b.id}>
                      <td><b>{b.name}</b></td>
                      <td className="s">{b.email}</td>
                      <td className="s">{b.passwortGesetzt ? (b.letzteAnmeldungAm ? `zuletzt ${datum(b.letzteAnmeldungAm)}` : 'noch nie angemeldet') : 'Passwort noch nicht gesetzt'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className="btn ghost sm"
                          onClick={async () => {
                            const a = await api.admin.benutzerLink(b.id);
                            setZugangslink({ fuer: b.email, link: a.link, meldung: a.meldung });
                          }}
                        >
                          Neuer Zugangslink
                        </button>
                      </td>
                    </tr>
                  ))}
                  {(benutzer[m.id] ?? []).length === 0 && (
                    <tr><td className="s">Noch kein Mitarbeiter.</td></tr>
                  )}
                </tbody>
              </table>
              <form onSubmit={(e) => einladen(m.id, e)} style={{ marginTop: 10 }}>
                <div className="feldreihe">
                  <label className="feld">
                    <span>E-Mail</span>
                    <input type="email" required value={einladung.email} onChange={(e) => setEinladung((a) => ({ ...a, email: e.target.value }))} />
                  </label>
                  <label className="feld">
                    <span>Name</span>
                    <input value={einladung.name} onChange={(e) => setEinladung((a) => ({ ...a, name: e.target.value }))} />
                  </label>
                </div>
                <button className="btn sm" type="submit">Mitarbeiter einladen</button>
              </form>
            </div>
          )}
        </div>
      ))}

      <h3 className="sec" style={{ marginTop: 22 }}>Betreiberzugänge</h3>
      <div className="card" style={{ padding: 16 }}>
        <table className="schlicht">
          <tbody>
            {betreiber.map((b) => (
              <tr key={b.id}>
                <td><b>{b.name}</b></td>
                <td className="s">{b.email}</td>
                <td className="s">{b.passwortGesetzt ? (b.letzteAnmeldungAm ? `zuletzt ${datum(b.letzteAnmeldungAm)}` : 'noch nie angemeldet') : 'Passwort noch nicht gesetzt'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <form onSubmit={betreiberEinladen} style={{ marginTop: 10 }}>
          <div className="feldreihe">
            <label className="feld">
              <span>E-Mail</span>
              <input type="email" required value={neuerBetreiber.email} onChange={(e) => setNeuerBetreiber((a) => ({ ...a, email: e.target.value }))} />
            </label>
            <label className="feld">
              <span>Name</span>
              <input value={neuerBetreiber.name} onChange={(e) => setNeuerBetreiber((a) => ({ ...a, name: e.target.value }))} />
            </label>
          </div>
          <button className="btn sm" type="submit">Betreiber einladen</button>
        </form>
      </div>

      {neu && (
        <div className="ovl" onMouseDown={(e) => e.target === e.currentTarget && setNeu(false)}>
          <div className="sheet">
            <h3>Hausverwaltung anlegen</h3>
            <form onSubmit={anlegen}>
              <label className="feld">
                <span>Name</span>
                <input required value={form.name} onChange={(e) => setForm((a) => ({ ...a, name: e.target.value }))} autoFocus placeholder="z. B. Immowerk Hausverwaltung GmbH" />
              </label>
              <label className="feld">
                <span>Kurzname (optional)</span>
                <input value={form.kurzname} onChange={(e) => setForm((a) => ({ ...a, kurzname: e.target.value }))} placeholder="z. B. Immowerk" />
              </label>
              <label className="feld">
                <span>iDWELL-API-Schlüssel (optional)</span>
                <input type="password" value={form.apiKey} onChange={(e) => setForm((a) => ({ ...a, apiKey: e.target.value }))} autoComplete="off" placeholder="leer = Belege aus Importpaket" />
                <small>Wird sofort gegen iDWELL geprüft und verschlüsselt gespeichert.</small>
              </label>
              <div className="feldreihe">
                <label className="feld">
                  <span>E-Mail der ersten Person</span>
                  <input type="email" value={form.adminEmail} onChange={(e) => setForm((a) => ({ ...a, adminEmail: e.target.value }))} placeholder="office@…" />
                </label>
                <label className="feld">
                  <span>Name</span>
                  <input value={form.adminName} onChange={(e) => setForm((a) => ({ ...a, adminName: e.target.value }))} />
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="btn ghost" type="button" onClick={() => setNeu(false)}>Abbrechen</button>
                <button className="btn primary" type="submit" disabled={laeuft || !form.name.trim()}>
                  {laeuft ? 'Wird angelegt …' : 'Anlegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
